import * as React from 'react'
import { Link } from 'react-router'
import { inject, observer } from 'mobx-react'
import { IComplianceAnalysis } from '@getgreenline/homi-shared'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { DebounceInput } from '../../../../components/Input'
import { Routes } from '../../../../constants/Routes'
import { ComplianceStore } from './ComplianceStore'
import { UncontrolledButtonDropdown, DropdownToggle, DropdownItem, DropdownMenu } from 'reactstrap'
import { LoadingAnimation } from '../../../../components/LoadingAnimation'
import { Button, Icon, message, Empty } from 'antd'
import { ONCompliancePreviewTable } from './PreviewTables/ON/ONCompliancePreviewTable'
import { parseErrorMsg } from '../../../../utilities/helpers'
import { BaseComplianceApi } from '@getgreenline/compliance'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  complianceStore: ComplianceStore
}

interface State {
  isLoading: boolean
  analysis: IComplianceAnalysis[]
  productSearch: string
  complianceCategoryId: number | undefined
  displayUnitText: DisplayText
  displayUnit: DisplayValue
  showDeleted: boolean
  sku: boolean
  category: boolean
  productFieldAll: boolean
  complianceAll: boolean
  cannabisWeight: boolean
  productWeight: boolean
  cannabisVolume: boolean
  complianceStartingInventory: boolean
  complianceSoldInStore: boolean
  complianceSoldOnline: boolean
  compliancePurchased: boolean
  complianceRefunded: boolean
  complianceTransferredAdditions: boolean
  complianceTransferredReductions: boolean
  complianceReturns: boolean
  complianceLost: boolean
  complianceDestroyed: boolean
  complianceOtherAdditions: boolean
  complianceOtherReductions: boolean
  complianceClosingInventory: boolean
  categoryName: string
}

enum DisplayValue {
  UNITS = 'unit',
  CANNABISWEIGHT = 'g',
  PRODUCTWEIGHT = 'g',
  VOLUME = 'ml',
  WHOLESALECOST = '$',
  RETAILPRICE = '$',
}

enum DisplayText {
  UNITS = 'Display: units',
  CANNABISWEIGHT = 'Display: Cannabis weight (g)',
  PRODUCTWEIGHT = 'Display: Net product weight (g)',
  VOLUME = 'Display: Cannabis volume (ml)',
  WHOLESALECOST = 'Display: Wholesale cost ($)',
  RETAILPRICE = 'Display: Retail price ($)',
}

enum ComplianceFieldsText {
  STARTING_INVENTORY = 'Starting inventory',
  SOLD_IN_STORE = 'Sold in-store',
  SOLD_ONLINE = 'Sold online',
  PURCHASE = 'Purchased',
  REFUNDED = 'Refunded',
  TRANSFERRED_ADDITIONS = 'Transferred additions',
  TRANSFERRED_REDUCTIONS = 'Transferred reductions',
  RETURNED = 'Returned',
  LOST = 'Lost',
  DESTROYED = 'Destroyed',
  OTHER_ADDITIONS = 'Other additions',
  OTHER_REDUCTIONS = 'Other reductions',
  CLOSING_INVENTORY = 'Closing inventory',
}

enum ComplianceFieldsMap {
  STARTING_INVENTORY = 'complianceStartingInventory',
  SOLD_IN_STORE = 'complianceSoldInStore',
  SOLD_ONLINE = 'complianceSoldOnline',
  PURCHASE = 'compliancePurchased',
  REFUNDED = 'complianceRefunded',
  TRANSFERRED_ADDITIONS = 'complianceTransferredAdditions',
  TRANSFERRED_REDUCTIONS = 'complianceTransferredReductions',
  RETURNED = 'complianceReturns',
  LOST = 'complianceLost',
  DESTROYED = 'complianceDestroyed',
  OTHER_ADDITIONS = 'complianceOtherAdditions',
  OTHER_REDUCTIONS = 'complianceOtherReductions',
  CLOSING_INVENTORY = 'complianceClosingInventory',
}

enum ProductFieldText {
  SKU = 'SKU',
  CATEGORY = 'Category',
  CANNABIS_WEIGHT = 'Cannabis weight',
  PRODUCT_WEIGHT = 'Net product weight',
  CANNABIS_VOLUME = 'Cannabis volume',
}

enum ProductFieldMap {
  SKU = 'sku',
  CATEGORY = 'category',
  CANNABIS_WEIGHT = 'cannabisWeight',
  PRODUCT_WEIGHT = 'productWeight',
  CANNABIS_VOLUME = 'cannabisVolume',
}

enum ShowDeletedValue {
  NON_DELETED = 0,
  DELETED = 1,
}

enum ShowDeletedText {
  NON_DELETED = 'Non-deleted',
  DELETED = 'Include deleted',
}

@inject('currentCompanyStore')
@observer
export class Analyze extends React.Component<Props, State> {
  company = this.props.currentCompanyStore!.company!

  constructor(props: Props) {
    super(props)
    this.state = {
      isLoading: false,
      analysis: [],
      productSearch: '',
      complianceCategoryId: undefined,
      categoryName: '',
      displayUnitText: DisplayText.UNITS,
      displayUnit: DisplayValue.UNITS,
      showDeleted: false,
      sku: true,
      category: true,
      complianceAll: false,
      productFieldAll: false,
      cannabisWeight: true,
      productWeight: true,
      cannabisVolume: false,
      complianceStartingInventory: true,
      complianceSoldInStore: true,
      compliancePurchased: true,
      complianceSoldOnline: false,
      complianceRefunded: false,
      complianceTransferredAdditions: false,
      complianceTransferredReductions: false,
      complianceReturns: false,
      complianceLost: false,
      complianceDestroyed: false,
      complianceOtherAdditions: false,
      complianceOtherReductions: false,
      complianceClosingInventory: true,
    }
  }

  async componentDidMount() {
    if (this.isOntario) {
      return
    }

    this.setState({ isLoading: true, analysis: [] })

    const { complianceStore } = this.props
    this.props.currentCompanyStore!.productStore!.getComplianceCategories()

    try {
      const complianceAnalysis = await BaseComplianceApi.getComplianceAnalysis(
        this.company.id,
        complianceStore.locationIds,
        complianceStore.year,
        complianceStore.month,
        complianceStore.timeFrame,
      )

      this.setState({
        analysis: complianceAnalysis,
      })
    } catch (err) {
      message.error(parseErrorMsg(err))
    } finally {
      this.setState({ isLoading: false })
    }
  }

  toggleComplianceSettings(toggle: boolean) {
    this.setState({
      complianceAll: toggle,
      complianceStartingInventory: toggle,
      complianceSoldInStore: toggle,
      compliancePurchased: toggle,
      complianceSoldOnline: toggle,
      complianceRefunded: toggle,
      complianceTransferredAdditions: toggle,
      complianceTransferredReductions: toggle,
      complianceReturns: toggle,
      complianceLost: toggle,
      complianceDestroyed: toggle,
      complianceOtherAdditions: toggle,
      complianceOtherReductions: toggle,
      complianceClosingInventory: toggle,
    })
  }

  toggleProductFieldSettings(toggle: boolean) {
    this.setState({
      productFieldAll: toggle,
      sku: toggle,
      category: toggle,
      cannabisWeight: toggle,
      cannabisVolume: toggle,
    })
  }

  computeUnitDisplay(quantity: number, product: IComplianceAnalysis) {
    const { displayUnitText } = this.state
    if (displayUnitText === DisplayText.UNITS) {
      return `${parseFloat(quantity.toFixed(2))}`
    } else if (displayUnitText === DisplayText.CANNABISWEIGHT) {
      return `${parseFloat((quantity * (product.productCannabisWeight || 0)).toFixed(2))}`
    } else if (displayUnitText === DisplayText.PRODUCTWEIGHT) {
      return `${parseFloat((quantity * (product.productWeight || 0)).toFixed(2))}`
    } else if (displayUnitText === DisplayText.VOLUME) {
      return `${parseFloat((quantity * (product.productCannabisVolume || 0)).toFixed(2))}`
    } else if (displayUnitText === DisplayText.WHOLESALECOST) {
      return `$${parseFloat(((quantity * (product.productPurchasePrice || 0)) / 100).toFixed(2))}`
    } else if (displayUnitText === DisplayText.RETAILPRICE) {
      return `$${parseFloat(((quantity * (product.productPrice || 0)) / 100).toFixed(2))}`
    } else {
      return `${parseFloat(quantity.toFixed(2))}`
    }
  }

  get isOntario(): boolean {
    return this.company.province === 'ON'
  }

  get renderAnalyzeTable() {
    const { productSearch, complianceCategoryId, showDeleted, analysis, displayUnit } = this.state

    let filteredAnalysis = analysis || []
    if (productSearch) {
      filteredAnalysis = filteredAnalysis.filter((p) => {
        const skuMatches = (p.sku || '').toLowerCase().includes(productSearch.toLowerCase())
        const nameMatches = (p.name || '').toLowerCase().includes(productSearch.toLowerCase())
        return skuMatches || nameMatches
      })
    }
    if (complianceCategoryId) {
      filteredAnalysis = filteredAnalysis.filter(
        (p) => p.complianceCategoryId === complianceCategoryId,
      )
    }
    if (!showDeleted) {
      filteredAnalysis = filteredAnalysis.filter((p) => !p.isDeleted)
    }

    if (filteredAnalysis.length === 0) {
      return <Empty />
    }

    return (
      <div className='table-responsive analyze_table'>
        <table className='table table-sm'>
          <thead className='thead-light'>
            <tr>
              {this.state.sku && <th>SKU</th>}
              <th>Name</th>
              {this.state.category && <th>Category</th>}
              {this.state.cannabisWeight && <th style={{ minWidth: '8rem' }}>Cannabis weight</th>}
              {this.state.productWeight && <th style={{ minWidth: '8rem' }}>Net product weight</th>}
              {this.state.cannabisVolume && <th style={{ minWidth: '8rem' }}>Cannabis volume</th>}
              {this.state.complianceStartingInventory && (
                <th className='text-right'>Opening ({displayUnit})</th>
              )}
              {this.state.complianceSoldInStore && (
                <th className='text-right'>Sold in-store ({displayUnit})</th>
              )}
              {this.state.complianceSoldOnline && (
                <th className='text-right'>Sold online ({displayUnit})</th>
              )}
              {this.state.compliancePurchased && (
                <th className='text-right'>Purchased ({displayUnit})</th>
              )}
              {this.state.complianceRefunded && (
                <th className='text-right'>Refunded ({displayUnit})</th>
              )}
              {this.state.complianceTransferredAdditions && (
                <th className='text-right'>Transferred additions ({displayUnit})</th>
              )}
              {this.state.complianceTransferredReductions && (
                <th className='text-right'>Transferred reductions ({displayUnit})</th>
              )}
              {this.state.complianceReturns && (
                <th className='text-right'>Returned ({displayUnit})</th>
              )}
              {this.state.complianceLost && <th className='text-right'>Lost ({displayUnit})</th>}
              {this.state.complianceDestroyed && (
                <th className='text-right'>Destroyed ({displayUnit})</th>
              )}
              {this.state.complianceOtherAdditions && (
                <th className='text-right'>Other additions ({displayUnit})</th>
              )}
              {this.state.complianceOtherReductions && (
                <th className='text-right'>Other reductions ({displayUnit})</th>
              )}
              {this.state.complianceClosingInventory && (
                <th className='text-right'>Closing ({displayUnit})</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredAnalysis.map((product) => {
              return (
                <tr key={product.id} className={product.isDeleted ? 'table-danger' : ''}>
                  {this.state.sku && <td style={{ maxWidth: 150 }}>{product.sku}</td>}
                  <td style={{ minWidth: 200 }}>
                    <Link
                      to={Routes.EDIT_PRODUCT(
                        this.company.id,
                        product.parentProductId || product.id,
                      )}
                    >
                      {product.name}
                    </Link>
                  </td>
                  {this.state.category && <td>{product.complianceCategoryName}</td>}
                  {this.state.cannabisWeight && (
                    <td>
                      {product.productCannabisWeight ? `${product.productCannabisWeight} g` : '-'}
                    </td>
                  )}
                  {this.state.productWeight && (
                    <td>{product.productWeight ? `${product.productWeight} g` : '-'}</td>
                  )}
                  {this.state.cannabisVolume && (
                    <td>
                      {product.productCannabisVolume ? ` ${product.productCannabisVolume} ml` : '-'}
                    </td>
                  )}
                  {this.state.complianceStartingInventory && (
                    <td className='text-right'>
                      {this.computeUnitDisplay(product.openingInventory, product)}
                    </td>
                  )}
                  {this.state.complianceSoldInStore && (
                    <td className='text-right'>
                      {this.computeUnitDisplay(product.soldInStore, product)}
                    </td>
                  )}
                  {this.state.complianceSoldOnline && (
                    <td className='text-right'>
                      {this.computeUnitDisplay(product.soldOnline, product)}
                    </td>
                  )}
                  {this.state.compliancePurchased && (
                    <td className='text-right'>
                      {this.computeUnitDisplay(product.purchased, product)}
                    </td>
                  )}
                  {this.state.complianceRefunded && (
                    <td className='text-right'>
                      {this.computeUnitDisplay(product.refunded, product)}
                    </td>
                  )}
                  {this.state.complianceTransferredAdditions && (
                    <td className='text-right'>
                      {this.computeUnitDisplay(product.transferredAdditions, product)}
                    </td>
                  )}
                  {this.state.complianceTransferredReductions && (
                    <td className='text-right'>
                      {this.computeUnitDisplay(product.transferredReductions, product)}
                    </td>
                  )}
                  {this.state.complianceReturns && (
                    <td className='text-right'>
                      {this.computeUnitDisplay(product.returned, product)}
                    </td>
                  )}
                  {this.state.complianceLost && (
                    <td className='text-right'>{this.computeUnitDisplay(product.lost, product)}</td>
                  )}
                  {this.state.complianceDestroyed && (
                    <td className='text-right'>
                      {this.computeUnitDisplay(product.destroyed, product)}
                    </td>
                  )}
                  {this.state.complianceOtherAdditions && (
                    <td className='text-right'>
                      {this.computeUnitDisplay(product.otherAdditions, product)}
                    </td>
                  )}
                  {this.state.complianceOtherReductions && (
                    <td className='text-right'>
                      {this.computeUnitDisplay(product.otherReductions, product)}
                    </td>
                  )}
                  {this.state.complianceClosingInventory && (
                    <td className='text-right'>
                      {this.computeUnitDisplay(product.closingInventory, product)}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  get renderFilterInputs() {
    const {
      productSearch,
      complianceCategoryId,
      showDeleted,
      complianceAll,
      categoryName,
      displayUnitText,
      productFieldAll,
    } = this.state
    const complianceCategories =
      this.props.currentCompanyStore!.productStore!.complianceCategories || []

    return (
      <div className='row mt-3 mb-3 p-3 mt-3 '>
        <div className='mr-2' style={{ width: 250 }}>
          <div className='input-group mb-2'>
            <div className='input-group-prepend'>
              <div className='input-group-text'>
                <span className='fa fa-search' />
              </div>
              <DebounceInput
                placeholder='Search product'
                value={productSearch}
                onChange={(e) => this.setState({ productSearch: e.target.value })}
                className='input-search-with-append'
              />
            </div>
          </div>
        </div>

        <div className='mr-2'>
          <UncontrolledButtonDropdown>
            <DropdownToggle caret color='light'>
              {categoryName || 'All categories'}
            </DropdownToggle>
            <DropdownMenu className='shadow animated fadeIn faster'>
              <DropdownItem
                onClick={() =>
                  this.setState({
                    complianceCategoryId: undefined,
                    categoryName: 'All categories',
                  })
                }
              >
                <input
                  type='radio'
                  checked={complianceCategoryId === undefined}
                  className='d-inline mr-2'
                />
                All
              </DropdownItem>
              <DropdownItem divider />
              {complianceCategories.map((complianceCategory) => {
                return (
                  <DropdownItem
                    onClick={(e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
                      this.setState({
                        complianceCategoryId: complianceCategory.id,
                        categoryName: complianceCategory.name,
                      })
                    }}
                    key={complianceCategory.id}
                  >
                    <input
                      type='radio'
                      checked={complianceCategoryId === complianceCategory.id}
                      className='d-inline mr-2'
                      value={complianceCategory.id}
                    />
                    {complianceCategory.name}
                  </DropdownItem>
                )
              })}
            </DropdownMenu>
          </UncontrolledButtonDropdown>
        </div>

        <div className='mr-2'>
          <UncontrolledButtonDropdown>
            <DropdownToggle caret color='light'>
              {showDeleted ? ShowDeletedText.DELETED : ShowDeletedText.NON_DELETED}
            </DropdownToggle>
            <DropdownMenu className='shadow animated fadeIn faster'>
              {Object.keys(ShowDeletedText).map((key, index) => {
                const id = `${key}-${index}`
                return (
                  <DropdownItem
                    key={id}
                    onClick={(e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
                      this.setState({ showDeleted: Boolean(ShowDeletedValue[key]) })
                    }}
                  >
                    <input
                      type='radio'
                      checked={showDeleted === Boolean(ShowDeletedValue[key])}
                      className='d-inline mr-2'
                    />
                    {ShowDeletedText[key]}
                  </DropdownItem>
                )
              })}
            </DropdownMenu>
          </UncontrolledButtonDropdown>
        </div>
        <div className='mr-2'>
          <UncontrolledButtonDropdown>
            <DropdownToggle caret color='light'>
              Product fields
            </DropdownToggle>
            <DropdownMenu className='shadow animated fadeIn faster' style={{ minWidth: '15rem' }}>
              <div
                className='ml-3 mr-2 p-2 cursor-pointer'
                onClick={() => this.toggleProductFieldSettings(!productFieldAll)}
              >
                <input type='checkbox' checked={productFieldAll} className='d-inline mr-2' />
                All
              </div>
              <DropdownItem divider />
              {Object.keys(ProductFieldText).map((key, index) => {
                const id = `${key}-${index}`
                const newProductFieldState = {
                  [ProductFieldMap[key]]: !this.state[ProductFieldMap[key]],
                }
                return (
                  <div
                    key={id}
                    className='ml-3 mr-2 p-2 cursor-pointer'
                    onClick={() =>
                      this.setState({
                        ...newProductFieldState,
                        productFieldAll: false,
                      })
                    }
                  >
                    <input
                      type='checkbox'
                      checked={this.state[ProductFieldMap[key]]}
                      className='d-inline mr-2'
                    />
                    {ProductFieldText[key]}
                  </div>
                )
              })}
            </DropdownMenu>
          </UncontrolledButtonDropdown>
        </div>

        <div className='mr-2'>
          <UncontrolledButtonDropdown>
            <DropdownToggle caret color='light'>
              Compliance fields
            </DropdownToggle>
            <DropdownMenu className='shadow animated fadeIn faster' style={{ minWidth: '15rem' }}>
              <div
                className='ml-3 mr-2 p-2 cursor-pointer'
                onClick={() => this.toggleComplianceSettings(!complianceAll)}
              >
                <input type='checkbox' checked={complianceAll} className='d-inline mr-2' />
                All
              </div>
              <DropdownItem divider />
              {Object.keys(ComplianceFieldsText).map((key, index) => {
                const id = `${key}-${index}`
                const newComplianceFieldsState = {
                  [ComplianceFieldsMap[key]]: !this.state[ComplianceFieldsMap[key]],
                }
                return (
                  <div
                    key={id}
                    className='ml-3 mr-2 p-2 cursor-pointer'
                    onClick={() =>
                      this.setState({
                        ...newComplianceFieldsState,
                        complianceAll: false,
                      })
                    }
                  >
                    <input
                      id={id + '-input'}
                      type='checkbox'
                      checked={this.state[ComplianceFieldsMap[key]]}
                      className='d-inline mr-2'
                    />
                    {ComplianceFieldsText[key]}
                  </div>
                )
              })}
            </DropdownMenu>
          </UncontrolledButtonDropdown>
        </div>

        <div className='mr-2'>
          <UncontrolledButtonDropdown>
            <DropdownToggle caret color='light'>
              {displayUnitText}
            </DropdownToggle>
            <DropdownMenu className='shadow animated fadeIn faster'>
              {Object.keys(DisplayText).map((key, index) => {
                const id = `${key}-${index}`
                return (
                  <DropdownItem
                    key={id}
                    onClick={(e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
                      this.setState({
                        displayUnitText: DisplayText[key],
                        displayUnit: DisplayValue[key],
                      })
                    }}
                  >
                    <input
                      type='radio'
                      checked={displayUnitText === DisplayText[key]}
                      className='d-inline mr-2'
                    />
                    {DisplayText[key]}
                  </DropdownItem>
                )
              })}
            </DropdownMenu>
          </UncontrolledButtonDropdown>
        </div>
      </div>
    )
  }

  render() {
    const { complianceStore } = this.props
    const { isLoading } = this.state

    return (
      <>
        <h5>
          {this.isOntario ? 'Federal compliance report preview' : 'Provincial report preview'}
        </h5>
        <div className='text-muted'>
          {`Check for anomalies before generating your final ${
            this.isOntario ? 'federal ' : ''
          }report`}
        </div>

        {!this.isOntario && (
          <>
            {this.renderFilterInputs}
            {!isLoading ? (
              <>
                {this.renderAnalyzeTable}
                <div>
                  <Button
                    type='primary'
                    onClick={() => complianceStore.fromCurrentStepToNextStep()}
                  >
                    Next <Icon type='right' style={{ marginLeft: 5 }} />
                  </Button>
                </div>
              </>
            ) : (
              <LoadingAnimation />
            )}
          </>
        )}

        {this.isOntario && (
          <>
            <ONCompliancePreviewTable complianceStore={complianceStore} />

            <Button type='primary' onClick={complianceStore.fromCurrentStepToNextStep}>
              Next <Icon type='right' style={{ marginLeft: 5 }} />
            </Button>
          </>
        )}
      </>
    )
  }
}
